// src/examples/Navbars/DashboardNavbar/index.js

import { useState, useEffect, useContext } from 'react';
import pLimit from 'p-limit';
import { useLocation, Link, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';

import {
  AppBar,
  Toolbar,
  IconButton,
  Icon,
  Tooltip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArgonBox from 'components/ArgonBox';

import Breadcrumbs from 'examples/Breadcrumbs';
import DynamicNotificationsMenu from 'components/DynamicNotificationsMenu';

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from 'examples/Navbars/DashboardNavbar/styles';

import { useArgonController, setTransparentNavbar, setMiniSidenav } from 'context';

import { UserContext } from 'context/userContext';
import { OrgContext } from 'context/orgContext';
import { refreshSalesforceToken } from 'services/salesforce';
import { userOrgs } from 'services/org';
import { saveOrgDetails } from 'services/org';
import { getConfig, saveConfigDetails } from 'services/org';
import { logout } from 'services/user';

// Import the new GlobalConfigEditor
import GlobalConfigEditor from 'components/GlobalConfigEditor'; // Adjust path if needed

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;
  const route = useLocation().pathname.split('/').slice(1);

  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');

  const { userId, setUserId } = useContext(UserContext); // Get setUserId to clear on logout
  const { setOrgs } = useContext(OrgContext); // <-- GET setOrgs from context

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const navigate = useNavigate(); // Hook for programmatic navigation

  // State for new Global Configuration Editor, initialized with empty arrays
  const [openGlobalConfigEditor, setOpenGlobalConfigEditor] = useState(false);
  const [globalConfig, setGlobalConfig] = useState({
    cpq: [],
    rc: [],
  });
  const [userOrgsList, setUserOrgsList] = useState([]); // Kept for refresh functionality

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType('sticky');
    } else {
      setNavbarType('static');
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener('scroll', handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener('scroll', handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowAlert(false);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await logout(); // Call the logout service

      setUserId(null);
      setOrgs(null);
      navigate('/authentication/sign-in'); // Redirect to sign-in page
    } catch (error) {
      setAlertMessage(`Logout failed: ${error.message}`);
      setAlertType('error');
      setShowAlert(true);
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    const limit = pLimit(2);

    try {
      const { orgs } = await userOrgs();
      if (!orgs || orgs.length === 0) {
        setAlertMessage('No organizations found to refresh.');
        setAlertType('warning');
        setShowAlert(true);
        return;
      }

      const responses = await Promise.all(
        orgs.map((org) =>
          limit(async () => {
            try {
              // Call the new, secure refresh token service
              await refreshSalesforceToken(org.org_id);
              return { success: true, name: org.name };
            } catch (e) {
              console.error(`Error refreshing org ${org.name}:`, e);
              return { success: false, name: org.name, error: e.message };
            }
          })
        )
      );

      const successfulResponses = responses.filter((r) => r.success);
      const failedResponses = responses.filter((r) => !r.success);

      if (failedResponses.length === 0 && successfulResponses.length > 0) {
        setAlertMessage('All connections refreshed successfully!');
        setAlertType('success');
      } else if (successfulResponses.length > 0 && failedResponses.length > 0) {
        setAlertMessage(`Some connections refreshed. Failed: ${failedResponses.map((r) => r.name).join(', ')}`);
        setAlertType('warning');
      } else if (failedResponses.length > 0) {
        setAlertMessage(
          `All connections failed to refresh. Errors: ${failedResponses.map((r) => `${r.name}: ${r.error}`).join('; ')}`
        );
        setAlertType('error');
      } else {
        setAlertMessage('No connections to refresh or all failed silently.'); // Should not happen if orgs exist
        setAlertType('warning');
      }
      setShowAlert(true);
    } catch (error) {
      setAlertMessage(`Error refreshing connections: ${error.message}`);
      setAlertType('error');
      setShowAlert(true);
      console.error('Error refreshing connections:', error);
    } finally {
      setIsLoading(false);
      setTimeout(() => setShowAlert(false), 5000);
    }
  };

  const handleOpenGlobalConfigEditor = async () => {
    setIsLoading(true);
    try {
      // Fetch user orgs (if needed for other context, not directly for global config)
      const { orgs } = await userOrgs();
      console.log();
      if (orgs.error) throw new Error(orgs.data.message || 'Failed to fetch user orgs');
      setUserOrgsList(orgs || []);

      // Fetch global configuration
      const { config } = await getConfig();
      if (config.error) throw new Error(config.data.message || 'Failed to fetch configuration');

      const fetchedConfig = config;
      // Add _tempId for React keys when loading
      const addTempIds = (arr) => arr.map((item) => ({ ...item, _tempId: Date.now() + Math.random() }));

      if (fetchedConfig && typeof fetchedConfig === 'object') {
        setGlobalConfig({
          cpq: Array.isArray(fetchedConfig.cpq) ? addTempIds(fetchedConfig.cpq) : [],
          rc: Array.isArray(fetchedConfig.rc) ? addTempIds(fetchedConfig.rc) : [],
        });
      } else {
        setGlobalConfig({
          cpq: [],
          rc: [],
        });
        setAlertMessage('No configuration found, starting with a blank slate.');
        setAlertType('info');
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
      }
      setOpenGlobalConfigEditor(true);
    } catch (error) {
      setAlertMessage(`Error opening configuration: ${error.message}`);
      setAlertType('error');
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      console.error('Error opening configuration:', error);
      // Fallback to empty arrays if fetch fails
      setGlobalConfig({
        cpq: [],
        rc: [],
      });
      setOpenGlobalConfigEditor(true); // Still open with a blank slate
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseGlobalConfigEditor = () => {
    setOpenGlobalConfigEditor(false);
  };

  const handleSaveGlobalConfig = async (updatedConfig) => {
    setIsLoading(true);
    try {
      await saveConfigDetails(updatedConfig);
      if (saveConfigDetails.error) throw new Error(saveConfigDetails.data.message || 'Failed to save configuration');

      // Add _tempId back for local state management after successful save
      const addTempIds = (arr) => arr.map((item) => ({ ...item, _tempId: Date.now() + Math.random() }));
      setGlobalConfig({
        cpq: addTempIds(updatedConfig.cpq),
        rc: addTempIds(updatedConfig.rc),
      });
      setAlertMessage('Global configuration saved successfully!');
      setAlertType('success');
      setShowAlert(true);
      handleCloseGlobalConfigEditor();
    } catch (error) {
      setAlertMessage(`Error saving global configuration: ${error.message}`);
      setAlertType('error');
      setShowAlert(true);
      console.error('Error saving global configuration:', error);
    } finally {
      setIsLoading(false);
      setTimeout(() => setShowAlert(false), 5000);
    }
  };

  return (
    <>
      <AppBar
        position={absolute ? 'absolute' : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
      >
        <Toolbar
          sx={(theme) => navbarContainer(theme, { navbarType })}
          style={{ background: 'white', padding: '1rem', borderRadius: '12pt' }}
        >
          <ArgonBox
            color={light && transparentNavbar ? 'white' : 'dark'}
            mb={{ xs: 1, md: 0 }}
            sx={(theme) => navbarRow(theme, { isMini })}
          >
            <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav} style={{ marginRight: '1rem' }}>
              {miniSidenav ? 'menu_open' : 'menu'}
            </Icon>
            <Breadcrumbs
              icon="home"
              title={route[route.length - 1]}
              route={route}
              light={transparentNavbar ? light : false}
            />
          </ArgonBox>
          {isMini ? null : (
            <ArgonBox sx={(theme) => navbarRow(theme, { isMini })}>
              <ArgonBox color={light ? 'white' : 'inherit'}>
                <Tooltip title="Configure Global Settings" arrow>
                  <IconButton
                    size="small"
                    color={light && transparentNavbar ? 'white' : 'dark'}
                    sx={navbarIconButton}
                    onClick={handleOpenGlobalConfigEditor} // Updated handler
                    disabled={isLoading} // Disable if any loading operation is in progress
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>

                {userId ? (
                  <>
                    <Tooltip title="Account Settings">
                      <IconButton onClick={handleProfileMenuOpen} sx={navbarIconButton} size="small" color="inherit">
                        <AccountCircleIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={handleProfileMenuClose}
                      MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                    >
                      <MenuItem component={Link} to="/profile" onClick={handleProfileMenuClose}>
                        <ListItemIcon>
                          <AccountCircleIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                          <Typography variant="p">My Profile</Typography>
                        </ListItemText>
                      </MenuItem>
                      <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>

                        <ListItemText>
                          <Typography variant="p">Logout</Typography>
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Link to="/authentication/sign-in/basic">
                    <IconButton sx={navbarIconButton} size="small">
                      <Icon
                        sx={({ palette: { dark, white } }) => ({
                          color: light && transparentNavbar ? white.main : dark.main,
                        })}
                      >
                        account_circle
                      </Icon>
                    </IconButton>
                  </Link>
                )}
                <IconButton
                  size="small"
                  color={light && transparentNavbar ? 'white' : 'dark'}
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon>{miniSidenav ? 'menu_open' : 'menu'}</Icon>
                </IconButton>

                <Tooltip title="Refresh Salesforce Connections" arrow>
                  <IconButton
                    size="small"
                    color={light && transparentNavbar ? 'white' : 'dark'}
                    sx={navbarIconButton}
                    onClick={handleRefresh}
                    disabled={isLoading}
                  >
                    <Icon>refresh</Icon>
                  </IconButton>
                </Tooltip>
                <DynamicNotificationsMenu />
              </ArgonBox>
            </ArgonBox>
          )}
        </Toolbar>
        {showAlert && (
          <Snackbar
            open={showAlert}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'top' }}
          >
            <Alert
              variant="filled"
              onClose={handleCloseSnackbar}
              severity={alertType}
              sx={{ alignItems: 'center', alignContent: 'center' }}
            >
              {alertMessage}
            </Alert>
          </Snackbar>
        )}
      </AppBar>

      {/* Render the new GlobalConfigEditor */}
      {openGlobalConfigEditor && (
        <GlobalConfigEditor
          open={openGlobalConfigEditor}
          onClose={handleCloseGlobalConfigEditor}
          initialConfig={globalConfig}
          onSave={handleSaveGlobalConfig}
          isLoading={isLoading}
        />
      )}
    </>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: true,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
